import { ReactNode } from "react";
import { useI18n } from "src/utils/lni18n";
import { StudentClassGroup, StudentClassGroups, studentClassGroups } from "./studentClassGroups";
import styles from "./StudentClassGroups.module.scss";

/**
 * The classes as a table with one tinted <tbody> per non empty group, headed by the group's name.
 * Stopped classes follow in a <details> of their own, folded initially: a <details> cannot sit
 * inside a table, so they get a second table with the same head.
 */
export function StudentClassGroupsTable<T extends { Id: string }>(props: {
    classes: StudentClassGroups<T> | undefined;
    /** The <thead>, if any; repeated on the stopped classes' table. */
    head?: ReactNode;
    columns: number;
    row: (cl: T) => ReactNode;
}) {
    const { languageService: t } = useI18n();
    const groups = studentClassGroups(props.classes);
    const open = groups.filter(g => g.key !== "Stopped");
    const stopped = groups.find(g => g.key === "Stopped");

    const body = (group: StudentClassGroup<T>, withHeading: boolean) => (
        <tbody key={group.key} className={styles[group.className]}>
            {withHeading && <tr>
                <th colSpan={props.columns} className="py-3 ps-3">
                    {t.getText(group.labelKey)}
                </th>
            </tr>}
            {group.classes.map(cl => <tr key={cl.Id}>{props.row(cl)}</tr>)}
        </tbody>
    );

    return <>
        {open.length > 0 && <table className="table table-condensed">
            {props.head}
            {open.map(g => body(g, true))}
        </table>}

        {stopped && <details className="mb-3">
            <summary className={`py-3 ps-3 fw-bold bg-${stopped.className}-subtle`}>
                {t.getText(stopped.labelKey)} ({stopped.classes.length})
            </summary>
            <table className="table table-condensed">
                {props.head}
                {body(stopped, false)}
            </table>
        </details>}
    </>;
}
